import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { announceStopped, type StoppedTimers } from '../timer/api';
import type { ProjectStatus } from '../projects/types';
import type { PhaseType, PhaseStatus } from '../phases/types';

export interface DashboardWeekPhase {
  id: string;
  type: PhaseType;
  status: PhaseStatus;
  plannedStart: string | null;
  plannedEnd: string | null;
  estimatedMinutes: number | null;
  actualMinutes: number;
  overdue: boolean;
  assignedTo: { id: string; fullName: string } | null;
  propItem: { id: string; name: string; project: { id: string; code: string } };
}

export interface DashboardActivity {
  id: string;
  body: string;
  createdAt: string;
  author: string;
  target: { kind: 'project' | 'propItem'; id: string; label: string } | null;
}

// Ranní fronta (backend: services/dashboardQueue.ts).
export type QueueItem =
  | { kind: 'blocked'; id: string; phaseType: PhaseType; propItemId: string; propItemName: string; projectCode: string; reason: string | null }
  | { kind: 'overdue_phase'; id: string; phaseType: PhaseType; propItemId: string; propItemName: string; projectCode: string; daysLate: number; assignee: string | null }
  | { kind: 'deadline'; id: string; code: string; name: string; deadline: string; daysUntil: number; openPhases: number }
  | { kind: 'handover'; id: string; code: string; name: string; count: number }
  | { kind: 'low_stock'; id: string; name: string; stock: number; reserved: number; min: number; unit: string }
  | { kind: 'client_decision'; id: string; projectId: string; code: string; title: string | null; version: number; accepted: boolean; decidedBy: string | null }
  | { kind: 'assign_quote'; id: string; projectId: string; code: string; title: string | null; version: number }
  | { kind: 'send_invoice'; id: string; number: string; label: string; link: string }
  | { kind: 'overdue_invoice'; id: string; number: string; label: string; link: string; daysOverdue: number; total: number; currency: string }
  | { kind: 'inquiry'; id: string; customerName: string; createdAt: string }
  | { kind: 'budget'; id: string; code: string; name: string; ordered: number; actual: number; currency: string };

export interface DashboardData {
  queue: QueueItem[];
  counts: {
    openProjects: number;
    totalProjects: number;
    propItems: number;
    materials: number;
    lowStock: number;
    blockedPhases: number;
    upcomingDeadlines: number;
    overdueInvoices: number;
    overduePhases: number;
  };
  warningDays: number;
  overdueInvoices: Array<{
    id: string;
    number: string;
    currency: string;
    totalPrice: number;
    dueAt: string | null;
    daysOverdue: number;
    label: string;
    link: string;
  }>;
  upcomingDeadlines: Array<{
    id: string;
    code: string;
    name: string;
    deadline: string | null;
    status: ProjectStatus;
    daysUntil: number | null;
  }>;
  blockedPhases: Array<{
    id: string;
    type: PhaseType;
    blockReason: string | null;
    propItem: { id: string; name: string; project: { id: string; code: string } };
  }>;
  lowStock: Array<{
    id: string;
    sku: string;
    name: string;
    unit: string;
    stockQty: string;
    minQty: string | null;
  }>;
  weekPhases: DashboardWeekPhase[];
  activity: DashboardActivity[];
  finance: {
    quotedTotal: number;
    actualCostTotal: number;
    profitTotal: number;
    projects: Array<{
      id: string;
      code: string;
      name: string;
      currency: string;
      quoted: number | null;
      actualCost: number;
      profit: number | null;
    }>;
  };
}

export function useDashboard() {
  return useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get<DashboardData>('/api/dashboard'),
    refetchInterval: 30_000,
  });
}

// Rychlý přechod stavu fáze přímo z dashboardu (invaliduje dashboard i gantt).
export function useDashboardPhaseTransition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, to }: { id: string; to: PhaseStatus }) =>
      api.post<StoppedTimers>(`/api/phases/${id}/transition`, { to }),
    onSuccess: (r) => {
      announceStopped(r);
      qc.invalidateQueries({ queryKey: ['timer'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: ['gantt'] });
    },
  });
}
